/**
 * The project's saved pipelines and console scripts, for the tab spec's
 * preparation: one query for the Data drawer's names and kinds, and a reader
 * the session calls at every preparation for the chosen sources.
 *
 * The names come from the plain list of saved queries, as the console lists
 * them: the structured list holds only records with a sidecar, so a script
 * saved before sidecars existed, or one whose sidecar was never written,
 * would be missing from it. A name takes its kind from its sidecar, and a
 * name without one is a script, as in the console. Tab spec files, builder
 * records and records of another kind are left out.
 *
 * Bodies are read only for the chosen names, and afresh at every
 * preparation, so a body saved in the console since is the one that runs.
 */
import { useCallback, useMemo } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "api/queryKeys"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import { isReservedQueryFileName } from "types/savedQuery.types"
import { PrepSource, SOURCES_NOT_LOADED } from "../runtime/preparation"

/** A saved pipeline or script without its body: what the Data drawer lists. */
export interface PrepListing {
  name: string
  kind: PrepSource["kind"]
}

/** The names the Data drawer offers, by kind. */
export interface PrepNames {
  pipelines: string[]
  scripts: string[]
}

export interface PreparationSources {
  /** The names of the saved pipelines and scripts; undefined while they load or when they could not be read. */
  names: PrepNames | undefined
  /**
   * Reads the chosen sources as they are now: the list again, then the body
   * of each chosen name it holds (a chosen name it does not hold is left
   * out). Rejects when the list cannot be read. A body that cannot be read
   * comes back empty.
   */
  read: (chosen: string[]) => Promise<PrepSource[]>
}

/** Same freshness as the console's saved-query list, for the drawer. */
const LIST_STALE_MS = 5_000

/**
 * Under the project's saved-query key, so anything that refreshes the
 * project's saved queries refreshes this too.
 */
export const preparationSourcesQueryKey = (projectId: string, owner: string) =>
  [...queryKeys.savedQueries.project(projectId, owner), "preparation"] as const

/** A refusal sent as bare text ("Error <code>: ..."), as the console reads it. */
const TEXT_REFUSAL = /^Error\s+\d+:/i

/**
 * The message of a Repos refusal: the Conductor answers HTTP 200 with
 * `{"Err msg": "..."}` (axios parses it), or an older server with the bare
 * text. Null when `body` is not one. The Console's Run on load list reads
 * the listing by the same rule.
 */
export const refusalOf = (body: unknown): string | null => {
  if (typeof body === "string") return TEXT_REFUSAL.test(body.trim()) ? body.trim() : null
  if (typeof body !== "object" || body === null) return null
  const msg = (body as Record<string, unknown>)["Err msg"]
  return typeof msg === "string" ? msg.trim() : null
}

type SavedQueriesApi = ReturnType<typeof useSavedQueries>

/** The project's saved pipelines and scripts, in the order the plain list gives them. */
const listSources = async (
  api: Pick<SavedQueriesApi, "getProjectQueries" | "listStructured">,
  projectId: string,
  owner: string
): Promise<PrepListing[]> => {
  const [plain, envelopes] = await Promise.all([
    api.getProjectQueries(projectId, owner),
    api.listStructured(projectId, owner),
  ])
  const body: unknown = plain.data
  const refused = refusalOf(body)
  if (refused !== null) throw new Error(refused)
  const kinds = new Map(envelopes.map((e) => [e.name, e.kind]))
  const names = (typeof body === "string" ? body : "")
    .split("\n")
    .map((n) => n.trim())
    .filter((n) => n && !isReservedQueryFileName(n))
  return [...new Set(names)].flatMap((name): PrepListing[] => {
    const kind = kinds.get(name) ?? "script"
    return kind === "pipeline" || kind === "script" ? [{ name, kind }] : []
  })
}

/** The body of saved query `name`, or "" when it cannot be read. */
const readBody = async (
  getQuery: SavedQueriesApi["getQuery"],
  projectId: string,
  owner: string,
  name: string
): Promise<string> => {
  try {
    const body: unknown = (await getQuery(projectId, owner, name)).data
    return typeof body === "string" && refusalOf(body) === null ? body : ""
  } catch {
    return ""
  }
}

/**
 * The saved pipelines and scripts of the project (see the file comment).
 * The drawer's list is fetched when a project is given; `read` always asks
 * the server again, which is also how a list that failed is read again.
 */
export const usePreparationSources = (
  projectId: string,
  owner: string
): PreparationSources => {
  const client = useQueryClient()
  const { getProjectQueries, listStructured, getQuery } = useSavedQueries()
  const list = useQuery({
    queryKey: preparationSourcesQueryKey(projectId, owner),
    queryFn: (): Promise<PrepListing[]> =>
      listSources({ getProjectQueries, listStructured }, projectId, owner),
    meta: { errorTitle: SOURCES_NOT_LOADED },
    enabled: Boolean(projectId && owner),
    staleTime: LIST_STALE_MS,
  })

  const names = useMemo(
    (): PrepNames | undefined =>
      list.data && {
        pipelines: list.data.filter((s) => s.kind === "pipeline").map((s) => s.name),
        scripts: list.data.filter((s) => s.kind === "script").map((s) => s.name),
      },
    [list.data]
  )

  const read = useCallback(
    async (chosen: string[]): Promise<PrepSource[]> => {
      // Read directly, not through the drawer's query: a failure here is the
      // session's to report (its alert says so), and the query would show
      // the notification a second time. A list read here is the drawer's too.
      const listed = await listSources({ getProjectQueries, listStructured }, projectId, owner)
      client.setQueryData(preparationSourcesQueryKey(projectId, owner), listed)
      const kinds = new Map(listed.map((s) => [s.name, s.kind]))
      const wanted = [...new Set(chosen)].filter((name) => kinds.has(name))
      return Promise.all(
        wanted.map(
          async (name): Promise<PrepSource> => ({
            name,
            kind: kinds.get(name) ?? "script",
            script: await readBody(getQuery, projectId, owner, name),
          })
        )
      )
    },
    [client, projectId, owner, getProjectQueries, listStructured, getQuery]
  )

  return useMemo(() => ({ names, read }), [names, read])
}
